/* Copyright (c) 2017 Backpack Health LLC */

/* global window */
import React from 'react';
import { FormattedMessage } from 'react-intl';
import cx from 'classnames';

import './ShareCardEdit.styles.scss';

import ProfileAvatar from '../../../components/ProfileAvatar';
import LocalizedGender from '../../../appAuthorized/presenters/LocalizedGender.redux';
import LocalizedBirthSex from '../../../appAuthorized/presenters/LocalizedBirthSex.redux';
import LocalizedBloodType from '../../../appAuthorized/presenters/LocalizedBloodType.redux';
import LocalizedRace from '../../../appAuthorized/presenters/LocalizedRace.redux';
import { BirthDateWithAge } from '../../../localization/LocalizedDate';

import DateUtil from '../../../../utilities/DateUtil';
import SharePhrases from '../../../../phraseKeys/Share.phrase';
import { ShareCard } from '../../../../models/ShareCardModel';
import { ShareCardCategory } from './ShareCardCategory';
import { ShareCardItemRow } from './ShareCardItemRow';
import { LocalizedResidence } from '../../../localization/LocalizedResidence';
import { useShareCardForActiveProfile } from '../../../hooks/useShareCard';
import { useActiveProfile } from '../../../hooks/useProfile';
import { useUIState } from '../../../hooks/useUIState';

const ProfileShareCardItemRow = ({ onToggle, label, shareCardItem, children, cssColor }) => {
  const isSelected = !shareCardItem.isExcluded();

  const titleColor = isSelected ? 'font-color-gsDarkest' : 'font-color-gsDark';
  const bodyColor = isSelected ? 'font-color-gsDark' : 'font-color-gsMedDark';
  return (
    <ShareCardItemRow
      cssColor={cssColor}
      isExcluded={shareCardItem.isExcluded()}
      isRequired={shareCardItem.isRequired()}
      isSelected={shareCardItem.isIncluded()}
      onToggle={onToggle}
    >
      <div className={cx('font-caption-1', titleColor)}>
        <FormattedMessage id={label}/>
      </div>
      <div className={cx('font-body', bodyColor)}>
        {isSelected ?
          children
          : <FormattedMessage id="shareCard.excluded.message.text" />
        }
      </div>
    </ShareCardItemRow>
  );
};

interface Props {
  isExpanded: boolean;
  toggleExpand(e): void;

  shareCardData: ShareCard;
  shareCardColorModel: any;
  onUpdateShareCard(d): void;
}

const ShareCardProfileGroup: React.FC<Props> = (props) => {
  const { onUpdateShareCard, shareCardData, shareCardColorModel, isExpanded, toggleExpand } = props;

  const [, scActions] = useShareCardForActiveProfile();
  const [, profileActions] = useActiveProfile();
  const [{ isActiveProfileMainProfile }] = useUIState();

  const shareCard = scActions.instantiate(shareCardData);
  const profile = profileActions.instantiateProfile();
  const cssColor = shareCardColorModel.cssColor();
  const profileTitleData = SharePhrases.profile(isActiveProfileMainProfile, profile.greetingName()).title;

  return (
    <ShareCardCategory
      group={shareCard.profile}
      showTotals={true}
      shareCardColorModel={shareCardColorModel}
      icon={shareCard.profilePhoto.isIncluded() ? () => <ProfileAvatar profile={profile} size={20} fontSize={8} /> : null}
      titlePhraseData={profileTitleData}
      isExpanded={isExpanded}
      toggleExpand={toggleExpand}
      toggleAll={() => {
        shareCard.profile.toggleAll();
        onUpdateShareCard(shareCard.data);
      }}
    >
      <ProfileShareCardItemRow
        label="profile.name.field.label"
        shareCardItem={shareCard.profileName}
        cssColor={cssColor}
        onToggle={() => {
          shareCard.profileName.toggle();
          onUpdateShareCard(shareCard.data);
        }}
      >
        {profile.fullName()}
      </ProfileShareCardItemRow>
      <ProfileShareCardItemRow
        label="profile.age.field.label"
        shareCardItem={shareCard.profileAge}
        cssColor={cssColor}
        onToggle={() => {
          shareCard.profileAge.toggle();
          onUpdateShareCard(shareCard.data);
        }}
      >
        <BirthDateWithAge
          profile={profile.data}
          today={DateUtil.today()}
          precision={shareCard.birthDatePrecision}
        />
      </ProfileShareCardItemRow>
      <ProfileShareCardItemRow
        label="profile.gender.field.label"
        shareCardItem={shareCard.profileGender}
        cssColor={cssColor}
        onToggle={() => {
          shareCard.profileGender.toggle();
          onUpdateShareCard(shareCard.data);
        }}
      >
        <LocalizedGender profile={profile} />
      </ProfileShareCardItemRow>
      <ProfileShareCardItemRow
        label="profile.birthSex.field.label"
        shareCardItem={shareCard.profileBirthSex}
        cssColor={cssColor}
        onToggle={() => {
          shareCard.profileBirthSex.toggle();
          onUpdateShareCard(shareCard.data);
        }}
      >
        <LocalizedBirthSex profile={profile} />
      </ProfileShareCardItemRow>
      <ProfileShareCardItemRow
        label="profile.residence.field.label"
        shareCardItem={shareCard.profileResidence}
        cssColor={cssColor}
        onToggle={() => {
          shareCard.profileResidence.toggle();
          onUpdateShareCard(shareCard.data);
        }}
      >
        {profile.hasValidResidence() ?
          <LocalizedResidence profile={profile} />
          :
          <FormattedMessage id="shareCardEdit.profileElement.notSpecified.text" />
        }
      </ProfileShareCardItemRow>
      <ProfileShareCardItemRow
        label="profile.race.field.label"
        shareCardItem={shareCard.profileRace}
        cssColor={cssColor}
        onToggle={() => {
          shareCard.profileRace.toggle();
          onUpdateShareCard(shareCard.data);
        }}
      >
        {profile.hasValidRace() ?
          <LocalizedRace profile={profile} secondaryClass="font-color-middle-gray"/>
          :
          <FormattedMessage id="shareCardEdit.profileElement.notSpecified.text" />
        }
      </ProfileShareCardItemRow>
      <ProfileShareCardItemRow
        label="profile.bloodType.field.label"
        shareCardItem={shareCard.profileBloodType}
        cssColor={cssColor}
        onToggle={() => {
          shareCard.profileBloodType.toggle();
          onUpdateShareCard(shareCard.data);
        }}
      >
        {profile.hasValidBloodType() ?
          <LocalizedBloodType profile={profile} />
          :
          <FormattedMessage id="shareCardEdit.profileElement.notSpecified.text" />
        }
      </ProfileShareCardItemRow>
      <ProfileShareCardItemRow
        label="profile.photo.title.text"
        shareCardItem={shareCard.profilePhoto}
        cssColor={cssColor}
        onToggle={() => {
          shareCard.profilePhoto.toggle();
          onUpdateShareCard(shareCard.data);
        }}
      >
        <FormattedMessage id={shareCard.profilePhoto.isIncluded() ? 'share.profilePhoto.visible.text' : 'share.profilePhoto.hidden.text'} />
      </ProfileShareCardItemRow>
    </ShareCardCategory>
  );
};

export default ShareCardProfileGroup;
